import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { examAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiTrash2, FiEye } from 'react-icons/fi';

const ExamQuestions = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExamQuestions();
  }, [examId]);

  const fetchExamQuestions = async () => {
    setLoading(true);
    try {
      const response = await examAPI.getExamQuestions(examId);
      if (response.data.success) {
        setExam(response.data.data.exam);
        setQuestions(response.data.data.questions || []);
      }
    } catch (error) {
      toast.error('Error fetching exam questions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (examQuestionId) => {
    if (!window.confirm('Remove this question from the exam?')) {
      return;
    }

    try {
      // This would need a backend endpoint to remove a question from exam
      // For now, we'll show a message
      toast.info('Remove question functionality - to be implemented on backend');
    } catch (error) {
      toast.error('Error removing question');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/exams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft size={20} />
          Back to Exams
        </button>
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {exam?.exam}
          </h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-600">Total Questions</p>
              <p className="text-xl font-semibold text-gray-900">{questions.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="text-xl font-semibold text-gray-900">{exam?.duration}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Marks</p>
              <p className="text-xl font-semibold text-gray-900">{exam?.marks}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Passing %</p>
              <p className="text-xl font-semibold text-gray-900">{exam?.passing_percentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Exam Questions</h3>
        </div>

        {questions.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 mb-4">No questions added to this exam yet</p>
            <button
              onClick={() => navigate('/exams')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Go back and add questions
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {questions.map((question, index) => (
              <div key={question.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium mb-3">{question.question}</p>
                        
                        <div className="space-y-2 mb-3">
                          {question.opt_a && (
                            <div className={`flex items-start gap-2 p-2 rounded ${question.correct === 'a' ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium text-gray-600">A.</span>
                              <span className={question.correct === 'a' ? 'text-green-700 font-medium' : 'text-gray-700'}>{question.opt_a}</span>
                              {question.correct === 'a' && <span className="ml-2 text-green-600 text-sm">✓ Correct</span>}
                            </div>
                          )}
                          {question.opt_b && (
                            <div className={`flex items-start gap-2 p-2 rounded ${question.correct === 'b' ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium text-gray-600">B.</span>
                              <span className={question.correct === 'b' ? 'text-green-700 font-medium' : 'text-gray-700'}>{question.opt_b}</span>
                              {question.correct === 'b' && <span className="ml-2 text-green-600 text-sm">✓ Correct</span>}
                            </div>
                          )}
                          {question.opt_c && (
                            <div className={`flex items-start gap-2 p-2 rounded ${question.correct === 'c' ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium text-gray-600">C.</span>
                              <span className={question.correct === 'c' ? 'text-green-700 font-medium' : 'text-gray-700'}>{question.opt_c}</span>
                              {question.correct === 'c' && <span className="ml-2 text-green-600 text-sm">✓ Correct</span>}
                            </div>
                          )}
                          {question.opt_d && (
                            <div className={`flex items-start gap-2 p-2 rounded ${question.correct === 'd' ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium text-gray-600">D.</span>
                              <span className={question.correct === 'd' ? 'text-green-700 font-medium' : 'text-gray-700'}>{question.opt_d}</span>
                              {question.correct === 'd' && <span className="ml-2 text-green-600 text-sm">✓ Correct</span>}
                            </div>
                          )}
                          {question.opt_e && (
                            <div className={`flex items-start gap-2 p-2 rounded ${question.correct === 'e' ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium text-gray-600">E.</span>
                              <span className={question.correct === 'e' ? 'text-green-700 font-medium' : 'text-gray-700'}>{question.opt_e}</span>
                              {question.correct === 'e' && <span className="ml-2 text-green-600 text-sm">✓ Correct</span>}
                            </div>
                          )}
                        </div>

                        {question.explanation && (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900 mb-1">Explanation:</p>
                            <p className="text-sm text-blue-800">{question.explanation}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-600">
                          {question.tradegroup_name && (
                            <span className="px-2 py-1 bg-gray-100 rounded">{question.tradegroup_name}</span>
                          )}
                          {question.trade_name && (
                            <span className="px-2 py-1 bg-gray-100 rounded">{question.trade_name}</span>
                          )}
                          {question.subject_name && (
                            <span className="px-2 py-1 bg-gray-100 rounded">{question.subject_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-1">
                    <button
                      onClick={() => navigate(`/questions/view/${question.id}`)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="View Details"
                    >
                      <FiEye size={16} />
                    </button>
                    <button
                      onClick={() => handleRemoveQuestion(question.exam_question_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove from Exam"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamQuestions;
